import React, { useEffect, useState, useMemo } from "react";
import { Table, Tag, Select, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getLeads } from "../features/customers/customerSlice";
import "../styles/leads.css";

const WhatsappLeadsPage = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getLeads());
  }, [dispatch]);

  const leadState = useSelector((state) => state.customer.leads) || [];
  const isLoading = useSelector((state) => state.customer.isLoading);

  const lostStatuses = [
    "bought_elsewhere",
    "not_interested",
    "no_budget",
    "no_response",
  ];

  const statusLabels = {
    new: "New",
    contacted: "Contacted",
    follow_up: "Follow Up",
    appointment_booked: "Appointment Booked",
    deal_closed: "Deal Closed",
    lost: "Lost",
    bought_elsewhere: "Lost - Bought Elsewhere",
    not_interested: "Lost - Not Interested",
    no_budget: "Lost - No Budget",
    no_response: "Lost - No Response",
  };

  /* ================= DATE HELPERS ================= */

  const now = new Date();
  const currentYear = now.getFullYear();

  const monthOptions = [
    { value: "all", label: "All Months" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const getWeekOfMonth = (dateString) => {
    const date = new Date(dateString);
    return Math.ceil(date.getDate() / 7);
  };

  /* ================= STATE ================= */

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(null);

  /* ================= FILTERED LEADS ================= */

  const filteredLeads = useMemo(() => {
    return leadState.filter((lead) => {
      const leadDate = new Date(lead.createdAt);
      const leadMonth = leadDate.getMonth();
      const leadWeek = getWeekOfMonth(lead.createdAt);

      let matchesStatus = true;
      let matchesMonth = true;
      let matchesWeek = true;

      if (selectedStatus) {
        matchesStatus =
          lead.status === selectedStatus ||
          (selectedStatus === "lost" &&
            (lead.status === "lost" || lostStatuses.includes(lead.status)));
      }

      if (selectedMonth !== "all") {
        matchesMonth = leadMonth === selectedMonth;
      }

      if (selectedWeek !== "all") {
        matchesWeek = leadWeek === selectedWeek;
      }

      return matchesStatus && matchesMonth && matchesWeek;
    });
  }, [leadState, selectedStatus, selectedMonth, selectedWeek]);

  const hasLeadData =
    Array.isArray(filteredLeads) && filteredLeads.length > 0;

  /* ================= KPI CALCULATIONS ================= */

  const totalLeads = filteredLeads.length;

  const contacted = filteredLeads.filter(
    (lead) => lead.status !== "new"
  ).length;

  const followUp = filteredLeads.filter(
    (lead) => lead.status === "follow_up"
  ).length;

  const appointmentBooked = filteredLeads.filter(
    (lead) => lead.status === "appointment_booked"
  ).length;

  const dealsClosed = filteredLeads.filter(
    (lead) => lead.status === "deal_closed"
  ).length;

  const dealsLost = filteredLeads.filter(
    (lead) =>
      lead.status === "lost" || lostStatuses.includes(lead.status)
  ).length;

  const displayNumber = (value) => {
    if (!hasLeadData) return "—";
    return Number(value || 0).toLocaleString();
  };

  /* ================= TABLE DATA ================= */

  const data1 = filteredLeads.map((lead, index) => ({
    key: index + 1,
    phone: lead.phone,
    name: lead.name || "Not provided",
    message: lead.message || "No message",
    assignedTo: lead.agentName || "Unassigned",
    status: lead.status,
    date: new Date(lead.createdAt).toLocaleDateString(),
  }));

  const columns = [
    { title: "SNo", dataIndex: "key" },
    { title: "Name", dataIndex: "name" },
    { title: "Message", dataIndex: "message", ellipsis: true },
    { title: "Assigned To", dataIndex: "assignedTo" },
    {
      title: "Status",
      render: (_, record) => {
        let color = "blue";

        if (record.status === "new") color = "green";
        if (record.status === "contacted") color = "orange";
        if (record.status === "follow_up") color = "purple";
        if (record.status === "appointment_booked") color = "cyan";
        if (record.status === "deal_closed") color = "blue";
        if (
          record.status === "lost" ||
          lostStatuses.includes(record.status)
        ) {
          color = "red";
        }

        return (
          <Tag color={color}>
            {statusLabels[record.status] || record.status}
          </Tag>
        );
      },
    },
    { title: "Date", dataIndex: "date" },
  ];

  const selectedMonthLabel =
    selectedMonth === "all"
      ? "All Months"
      : monthOptions.find((month) => month.value === selectedMonth)?.label;

  const selectedWeekLabel =
    selectedWeek === "all" ? "All Weeks" : `Week ${selectedWeek}`;

  const weekOptions = [
    { value: "all", label: "All Weeks" },
    ...Array.from({ length: 5 }, (_, i) => ({
      value: i + 1,
      label: `Week ${i + 1}`,
    })),
  ];
const renderKpiSkeletons = () => (
  <div className="mb-4 leads__count-wrapper">
    {Array.from({ length: 6 }).map((_, index) => (
      <div className="col-md-3 d-flex" key={index}>
        <div
          className="bg-white p-3 rounded-3 flex-grow-1 d-flex shadow-sm"
          style={{
            flexDirection: "column",
            gap: 12,
          }}
        >
          <Skeleton.Input
            active
            size="small"
            style={{ width: 110 ,height:15}}
          />
          <Skeleton.Input
            active
            size="default"
            style={{ width: 60 ,height:25}}
          />
        </div>
      </div>
    ))}
  </div>
);
  return (
    <div>

      <h3 className="mb-1 title">Leads Overview</h3>

      <p className="text-muted mb-4">
        Showing Data for:{" "}
        <strong>
          {selectedMonthLabel} - {selectedWeekLabel}, {currentYear}
        </strong>
      </p>

      <div className="d-flex gap-3 mb-3 flex-wrap">
        <Select
          placeholder="Filter by Month"
          style={{ width: 180 }}
          value={selectedMonth}
          onChange={(value) => {
            setSelectedMonth(value);
            setSelectedWeek("all");
          }}
          options={monthOptions}
        />

        <Select
          placeholder="Filter by Week"
          style={{ width: 180 }}
          value={selectedWeek}
          onChange={(value) => setSelectedWeek(value)}
          options={weekOptions}
        />

        <Select
          placeholder="Filter by Status"
          style={{ width: 200 }}
          allowClear
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
          options={[
            { value: "new", label: "New" },
            { value: "contacted", label: "Contacted" },
            { value: "follow_up", label: "Follow Up" },
            { value: "appointment_booked", label: "Appointment Booked" },
            { value: "deal_closed", label: "Deal Closed" },
            { value: "lost", label: "Lost" },
          ]}
        />
      </div>

      {isLoading && leadState.length === 0 ? (
        renderKpiSkeletons()
      ) : (
        <div className="mb-4 leads__count-wrapper">
          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #4f46e5" }}
            >
              <small style={{ color: "#4f46e5" }}>Total Leads</small>
              <h4 className="mb-0" style={{ color: "#1e1b4b" }}>
                {displayNumber(totalLeads)}
              </h4>
            </div>
          </div>

          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #0891b2" }}
            >
              <small style={{ color: "#0891b2" }}>Contacted</small>
              <h4 className="mb-0" style={{ color: "#164e63" }}>
                {displayNumber(contacted)}
              </h4>
            </div>
          </div>

          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #ea580c" }}
            >
              <small style={{ color: "#ea580c" }}>Follow Ups</small>
              <h4 className="mb-0" style={{ color: "#7c2d12" }}>
                {displayNumber(followUp)}
              </h4>
            </div>
          </div>

          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #dc2626" }}
            >
              <small style={{ color: "#dc2626" }}>Deals Lost</small>
              <h4 className="mb-0" style={{ color: "#7f1d1d" }}>
                {displayNumber(dealsLost)}
              </h4>
            </div>
          </div>

          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #7c3aed" }}
            >
              <small style={{ color: "#7c3aed" }}>Appointment Booked</small>
              <h4 className="mb-0" style={{ color: "#4c1d95" }}>
                {displayNumber(appointmentBooked)}
              </h4>
            </div>
          </div>

          <div className="col-md-3 d-flex">
            <div
              className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
              style={{ borderLeft: "5px solid #16a34a" }}
            >
              <small style={{ color: "#16a34a" }}>Deals Closed</small>
              <h4 className="mb-0" style={{ color: "#14532d" }}>
                {displayNumber(dealsClosed)}
              </h4>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-3">
        <Table
          columns={columns}
          dataSource={data1}
          loading={isLoading && leadState.length === 0}
        />
      </div>
    </div>
  );
};

export default WhatsappLeadsPage;